import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { trackEvent } from '../../lib/analytics';

const BLOG_POST_URL = 'https://noctua.uno/blog/getting-started-with-noctua';

interface OnboardingModalProps {
  onComplete: () => void;
  rssFeedToken?: string;
  onConvert: (url: string) => Promise<void>;
}

const STEPS = [
  {
    id: 'concept',
    label: '01',
  },
  {
    id: 'try-it',
    label: '02',
  },
  {
    id: 'rss-feed',
    label: '03',
  },
] as const;

export default function OnboardingModal({ onComplete, rssFeedToken, onConvert }: OnboardingModalProps) {
  const [step, setStep] = useState(0);
  const [conversionUrl, setConversionUrl] = useState(BLOG_POST_URL);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const isLastStep = step === STEPS.length - 1;

  const handleNext = () => {
    trackEvent('onboarding_step', { step: STEPS[step].id, action: isLastStep ? 'complete' : 'next' });
    if (step === 1 && conversionUrl.trim()) {
      onConvert(conversionUrl.trim()).catch(() => {});
    }
    if (isLastStep) {
      onComplete();
    } else {
      setStep((s) => s + 1);
    }
  };

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const baseUrl = apiUrl.replace(/\/api\/?$/, '') || window.location.origin;
  const feedUrl = rssFeedToken ? `${baseUrl}/feed/${rssFeedToken}` : '';

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-card border border-border rounded-sm max-w-lg w-full animate-fade-in-up">

          {/* Step indicator */}
          <div className="flex items-center gap-2 px-8 pt-8 pb-0">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`h-0.5 flex-1 transition-colors duration-300 ${
                  i <= step ? 'bg-primary' : 'bg-border'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="px-8 pt-6 pb-8">
            {step === 0 && <StepConcept />}
            {step === 1 && <StepTryIt url={conversionUrl} onUrlChange={setConversionUrl} />}
            {step === 2 && <StepRssFeed feedUrl={feedUrl} />}

            {/* Actions */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={() => {
                  trackEvent('onboarding_step', { step: STEPS[step].id, action: 'skip' });
                  onComplete();
                }}
                className="font-mono text-xs tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip
              </button>
              <div className="flex items-center gap-4">
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="bg-primary text-primary-foreground font-body text-sm font-medium px-6 py-2.5 rounded-sm hover:opacity-90 transition-opacity"
                >
                  {isLastStep ? 'Begin →' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StepConcept() {
  return (
    <div>
      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-6">
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      </div>

      <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-2">
        Welcome to noctua
      </p>
      <h2 className="font-display text-3xl font-normal text-foreground mb-4">
        Text to speech, in your podcast app
      </h2>
      <p className="font-body text-muted-foreground leading-relaxed mb-8">
        Paste a URL from any article, blog post, or newsletter. noctua reads it aloud in a natural voice and adds the audio to your personal podcast feed.
      </p>

      <div className="space-y-4">
        {[
          {
            step: '01',
            text: 'Paste any source URL',
            icon: (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            ),
          },
          {
            step: '02',
            text: 'noctua reads it aloud in a natural voice',
            icon: (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
              />
            ),
          },
          {
            step: '03',
            text: 'Listen in any podcast app — your feed only',
            icon: (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            ),
          },
        ].map(({ step: s, text, icon }) => (
          <div key={s} className="flex items-center gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-muted flex items-center justify-center">
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {icon}
              </svg>
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-xs text-muted-foreground flex-shrink-0">{s}</span>
              <span className="font-body text-sm text-foreground">{text}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepTryIt({ url, onUrlChange }: { url: string; onUrlChange: (url: string) => void }) {
  return (
    <div>
      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-6">
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      </div>

      <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-2">
        Your first conversion
      </p>
      <h2 className="font-display text-3xl font-normal text-foreground mb-4">
        Make your first conversion
      </h2>
      <p className="font-body text-muted-foreground leading-relaxed mb-6">
        We have pre-filled our getting started guide below — or paste any source URL you like. Hit <span className="text-foreground font-medium">Next</span> and noctua starts converting it to audio. Each conversion uses one credit.
      </p>

      {/* Editable URL input */}
      <div className="bg-muted/40 border border-border rounded-sm p-4 mb-6">
        <p className="font-mono text-xs tracking-wide uppercase text-muted-foreground mb-3">
          Source URL
        </p>
        <div className="flex items-center gap-3 bg-background border border-border rounded-sm px-4 py-3 focus-within:border-primary transition-colors">
          <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          <input
            type="url"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://..."
            className="flex-1 bg-transparent font-body text-sm text-foreground placeholder:text-muted-foreground outline-none min-w-0"
          />
        </div>
        <p className="font-mono text-xs text-muted-foreground mt-3 text-right">1 credit per conversion</p>
      </div>

      <p className="font-body text-sm text-muted-foreground">
        You can also{' '}
        <span className="text-foreground font-medium">forward newsletters by email</span>{' '}
        — any email sent to{' '}
        <span className="font-mono text-xs">carrier-pigeon@noctua.uno</span>{' '}
        is converted to audio automatically. The Obsidian plugin and any MCP-compatible AI assistant can send to your feed too.
      </p>
    </div>
  );
}

function StepRssFeed({ feedUrl }: { feedUrl: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      trackEvent('feed_url_copied', { surface: 'onboarding' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-6">
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
          />
        </svg>
      </div>

      <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-2">
        Listen anywhere
      </p>
      <h2 className="font-display text-3xl font-normal text-foreground mb-4">
        Add to Apple Podcasts
      </h2>
      <p className="font-body text-muted-foreground leading-relaxed mb-6">
        Every conversion appears as an episode in your private podcast feed. Copy your feed URL below and add it to Apple Podcasts, Pocket Casts or Overcast — new episodes appear automatically. Spotify doesn't support custom feeds.
      </p>

      {/* Feed URL */}
      <div className="bg-muted/40 border border-border rounded-sm p-4 mb-6">
        <p className="font-mono text-xs tracking-wide uppercase text-muted-foreground mb-3">
          Your personal feed URL
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-3 bg-background border border-border rounded-sm px-4 py-3 min-w-0">
            <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            </svg>
            <span className="font-mono text-xs text-muted-foreground truncate">
              {feedUrl || 'Loading…'}
            </span>
          </div>
          <button
            onClick={handleCopy}
            disabled={!feedUrl}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-3 rounded-sm font-body text-xs font-medium transition-colors ${
              copied
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Apple Podcasts steps */}
      <div className="space-y-3">
        {[
          { n: '01', text: 'Open Apple Podcasts on your iPhone or Mac' },
          { n: '02', text: 'Tap the library icon, then tap the 3 dots button on the top right, next to the profile image.' },
          { n: '03', text: 'Tap "Follow a Show by URI"' },
          { n: '04', text: 'Paste your feed URL and tap Follow' },
          { n: '05', text: 'New episodes appear automatically as you convert more' },
        ].map(({ n, text }) => (
          <div key={n} className="flex items-start gap-4">
            <span className="font-mono text-xs text-muted-foreground flex-shrink-0 mt-0.5">{n}</span>
            <span className="font-body text-sm text-foreground leading-relaxed">{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
